import { useState } from 'react';

import { loaders } from 'entities';

import { Box, Button, Column, Modal, Row } from 'components/Blocks';
import { MessageCircle } from 'components/Blocks/Icons';
import { Coin } from 'components/DecorativeIcons';

import { useAuth, useUser } from 'providers/Auth';
import { useToggleV2 } from 'hooks';

import { PhoneForm } from './PhoneForm';
import { ConfirmWppForm } from './ConfirmForm';

import { phoneByCountry } from './utils';
import { cascadeEval } from 'utils/conditions';
import { useLang } from 'locale';
import { cn } from 'styles';

export function WppConfirm() {
  const { t } = useLang();
  const { updateUser } = useAuth();
  const { phone, country } = useUser();

  const [codeSent, codeSentHandle] = useToggleV2();
  const [confirmed, confirmedHandle] = useToggleV2();
  const [wpp, setWpp] = useState('');

  const [{ enabled, rewardPoints }] = loaders.whatsapp.canConfirm.useV2({
    defaultValue: { enabled: false, rewardPoints: 0 },
    disabled: !!phone,
    refreshMinutes: 60,
    refetchOnMount: false,
  });

  const phoneConfig = phoneByCountry[country];

  if (phone || !enabled || !phoneConfig) return null;

  const maskedPhone = phoneConfig.mask(wpp);

  const closeAfterConfirm = () => {
    updateUser({ phone: wpp });
  };

  const changeNumber = () => {
    codeSentHandle.turnOff();
    setWpp('');
  };

  return (
    <Modal
      allowEscape={false}
      showCloseButton={!confirmed}
      trigger={
        <button
          className={cn(
            'flex items-center gap-2 rounded-lg border bg-background px-3 py-2 transition-colors hover:border-primary hover:bg-primary/5',
            'mx-auto small_tablet:w-[calc(100%-2rem)]',
          )}
        >
          <Box className="bg-primary p-2 text-primary-foreground" border="circle">
            <MessageCircle size={16} />
          </Box>

          <Column gap="1">
            <p className="font-semibold text-primary">{t('verify-whatsapp')}</p>

            <Row gap="2">
              <Coin size={18} />
              <span className="mt-1 text-sm font-medium leading-none text-warning">
                {t('+points', { rewardPoints })}
              </span>
            </Row>
          </Column>
        </button>
      }
    >
      {cascadeEval([
        {
          is: confirmed,
          then: () => (
            <Column
              alignItems="center"
              className="min-w-64 duration-300 animate-in fade-in-0 slide-in-from-bottom-11"
            >
              <Coin size={36} />

              <h3 className="text-warning">{t('+points', { rewardPoints })}</h3>

              <p className="text-center font-semibold text-primary">{t('whatsapp-confirmed')}</p>

              <Button className="w-full" onClick={closeAfterConfirm}>
                {t('close')}
              </Button>
            </Column>
          ),
        },
        {
          is: codeSent,
          then: () => (
            <ConfirmWppForm
              phone={maskedPhone}
              onConfirm={confirmedHandle.turnOn}
              onChangeNumber={changeNumber}
              rewardPoints={rewardPoints}
            />
          ),
        },
        {
          is: true,
          then: () => (
            <PhoneForm
              wpp={wpp}
              maskedPhone={maskedPhone}
              setWpp={setWpp}
              onSent={codeSentHandle.toggle}
            />
          ),
        },
      ])}
    </Modal>
  );
}
